import React, { useState, useMemo } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import { DataTable } from "@/components/common/Table";
import { columns, type RecentVisit } from "./recentVisitsColumn";
import { routes } from "@/utils/urls";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import RecentVisitsIcon from "@/assets/icons/recent-visits.svg";
import { Typography } from "@/components/ui/typography";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { Button } from "@/components/ui/button";
import RecentVisitsDialog from "./RecentVisitsDialog";
import { useFetchCard } from "@/hooks/useFetchCard";

interface RecentVisitsResponse {
  status: number;
  data: {
    date: string;
    type: string;
    facility: string | null;
    diagnosis?: string;
  }[];
}

interface RecentVisitsCardProps {
  shouldBlink?: boolean;
}

const RecentVisitsCard: React.FC<RecentVisitsCardProps> = ({ shouldBlink = false }) => {
  const {
    cardVariant,
    isFavourite,
    toggleFavourite,
  } = useDashboardCardFavorite("RECENT_VISITS_ENCOUNTERS");
  const [dialogOpen, setDialogOpen] = useState(false);


  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const { data: recentVisitsResponse, loading, error, isErrorRed } = useFetchCard<RecentVisitsResponse['data']>({
    endpoint: `${routes.cards.recentVisits('').replace(/\/$/, '')}`,
    memberId,
    method: 'POST',
    requestBody: { view: "card" },
    processData: (rawData: unknown) => (rawData as RecentVisitsResponse).data,
  });

  const visitsData = useMemo(() => {
    if (!recentVisitsResponse || recentVisitsResponse.length === 0) return [];
    return recentVisitsResponse.map((item, index) => ({
      id: index + 1,
      date: item.date,
      type: item.type,
      facility: item.facility || "",
      diagnosis: item.diagnosis || "",
    }));
  }, [recentVisitsResponse]);

  const handleViewAll = () => {
    setDialogOpen(true);
  };

  return (
    <>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
        <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
          <DashboardCardHeader
            icon={
              <BorderedIconWrapper>
                <img
                  src={RecentVisitsIcon}
                  alt='AdtAlertsIcon'
                  className='w-4 h-4 block'
                />
              </BorderedIconWrapper>
            }
            title='Recent Visits / Encounters'
            className='h-[56px]'
            showStar={true}
            button={
              <Button
                variant='button'
                className='!h-[28px] !px-3 !py-1 !text-sm mb-[2px]'
                onClick={handleViewAll}
                disabled={loading || !!error || visitsData.length === 0}>
                View all
              </Button>
            }
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
          />
          <CardContent className='p-4 lg:h-[304px]'>
            {error ? (
              <div className='flex items-center justify-center h-full lg:h-[304px] md:h-[304px]'>
                <Typography
                  variant='medium-14'
                  className={`${
                    isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                  } font-medium`}>
                  {error}
                </Typography>
              </div>
            ) : (
              <DataTable<RecentVisit>
                data={{
                  data: visitsData,
                  total: visitsData.length,
                }}
                columns={columns}
                height='auto'
                maxHeight='247px'
                isLoading={loading}
                skeletonCount={4}
              />
            )}
          </CardContent>
        </DashboardCardWrapper>
      </div>
      <RecentVisitsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        memberId={memberId}
      />
    </>
  );
};

export default RecentVisitsCard;
